import * as Sentry from '@sentry/nextjs';
import { submitLeadFormData } from '@/lib/lead-submission';
import type { HiddenField } from '@/components/gateway-forms/types';
import { GENDER, SALUTATION } from '@/sections/forms/multistep-form/form/types';
import { getPreferredLanguage } from '@/components/gateway-forms/util/utils';

type MultistepMeta = {
  consultationTitle?: string | null;
  topics?: string[] | null;
  anliegen?: string;
};

type SubmitFormOptions = {
  multistepMeta?: MultistepMeta;
  presetCallbackRequestNotes?: string;
  presetWebsiteSelectedTopics?: string;
  hiddenFields?: HiddenField[];
};

const isDefault = (v?: string | null) =>
  String(v ?? '')
    .trim()
    .toLowerCase() === 'default';

const normalizeTopics = (topics?: string[] | null) =>
  (topics ?? []).map((t) => t.trim()).filter(Boolean);

const buildDefaultUtmParameter = (date = new Date()) => {
  const yy = String(date.getFullYear()).slice(-2);
  return `tv${yy}_qu`;
};

export const getUtmSourceData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const sourceKey = urlParams.get('utm_sourcekey') || '';
  const sourceUrl = window.location.href || 'https://smzh.ch/de/';
  return { sourceKey, sourceUrl };
};

const getEffectiveTopics = (values: any, meta?: MultistepMeta) => {
  const metaTopics = normalizeTopics(meta?.topics);
  if (metaTopics.length) return metaTopics;

  const v =
    values?.multistepTopics ??
    values?.selectedTopics ??
    values?.Website_Selected_Topics__c;

  if (Array.isArray(v)) return normalizeTopics(v);
  if (typeof v === 'string')
    return v
      .split(',')
      .map((x: string) => x.trim())
      .filter(Boolean);

  return [];
};

const getEffectiveConsultationTitle = (values: any, meta?: MultistepMeta) => {
  const metaTitle = meta?.consultationTitle?.trim() ?? '';
  if (metaTitle && !isDefault(metaTitle)) return metaTitle;

  const v = (
    values?.multistepConsultationTitle ??
    values?.consultationTitle ??
    ''
  ).trim();
  if (v && !isDefault(v)) return v;

  return '';
};

const buildWebsiteSelectedTopics = (values: any, meta?: MultistepMeta) =>
  getEffectiveTopics(values, meta).join(', ');

const buildCallbackRequestNotes = (
  values: any,
  radioGroupNotes: string[],
  selectGroupNotes: string[],
  textInputsNotes: string[],
  meta?: MultistepMeta,
) => {
  const lines: string[] = [];

  if (meta?.anliegen?.trim()) lines.push(meta.anliegen.trim());

  const consultationTitle = getEffectiveConsultationTitle(values, meta);
  if (consultationTitle) {
    lines.push(`Beratungsart:\n-${consultationTitle}`);
  }

  const topics = getEffectiveTopics(values, meta);
  if (topics.length) {
    lines.push(`Themen:\n${topics.map((t) => `-${t}`).join('\n')}`);
  }

  lines.push(...selectGroupNotes, ...radioGroupNotes, ...textInputsNotes);

  return lines.filter(Boolean).join('\n');
};

const appendHiddenFields = (
  formData: FormData,
  hiddenFields: HiddenField[] = [],
  reservedKeys: Set<string>,
) => {
  const appendedKeys = new Set(reservedKeys);

  hiddenFields
    .map(({ key, value }) => ({
      key: String(key ?? '').trim(),
      value: value ?? '',
    }))
    .filter(({ key }) => Boolean(key))
    .forEach(({ key, value }) => {
      if (appendedKeys.has(key)) return;
      formData.append(key, value?.toString() || '');
      appendedKeys.add(key);
    });
};

export const submitFormData = async (
  values: any,
  radioGroups: any[] = [],
  selectGroups: any[] = [],
  textInputs: Array<{ name?: string; label?: any }> = [],
  options: SubmitFormOptions = {},
) => {
  const {
    multistepMeta,
    presetCallbackRequestNotes,
    presetWebsiteSelectedTopics,
    hiddenFields,
  } = options;
  const radioGroupNotes = radioGroups
    .map((group) => ({ label: group.label, value: values[group.name] }))
    .filter((item) => item.value)
    .map((item) => `${item.label}\n-${item.value}`);

  const selectGroupNotes = selectGroups
    .map((group) => ({ label: group.label, value: values[group.name] }))
    .filter((item) => item.value)
    .map((item) => `${item.label}\n-${item.value}`);

  const textInputsNotes = textInputs
    .map((input) => ({
      label: input.label,
      value: input.name ? values[input.name] : undefined,
    }))
    .filter((item) => item.value)
    .map((item) => `${item.label}\n-${item.value}`);

  const derivedWebsiteSelectedTopics = buildWebsiteSelectedTopics(
    values,
    multistepMeta,
  );

  const derivedCallbackRequestNotes = buildCallbackRequestNotes(
    values,
    radioGroupNotes,
    selectGroupNotes,
    textInputsNotes,
    multistepMeta,
  );

  const websiteSelectedTopics =
    presetWebsiteSelectedTopics && !isDefault(presetWebsiteSelectedTopics)
      ? presetWebsiteSelectedTopics
      : derivedWebsiteSelectedTopics;

  const callbackRequestNotes =
    presetCallbackRequestNotes && !isDefault(presetCallbackRequestNotes)
      ? presetCallbackRequestNotes
      : derivedCallbackRequestNotes;

  const { sourceUrl, sourceKey } = getUtmSourceData();
  const hiddenFieldMap = Object.fromEntries(
    (hiddenFields ?? []).map((field) => [field.key, field.value]),
  );
  const utmParameter =
    sourceKey || hiddenFieldMap.UTM_Parameter__c || buildDefaultUtmParameter();

  const formattedData = {
    Salutation:
      values.gender === GENDER.MALE ? SALUTATION.HERR : SALUTATION.FRAU,
    LastName: values.surname,
    FirstName: values.name,
    Email: values.email,
    MobilePhone: values.phoneNumber,
    nx_Geburtsdatum__c: values.birthDate,
    CallbackWindowTime__c: values.recallTime,
    CallbackRequestedDate__c: values.recallDate,
    CallbackRequestNotes__c: callbackRequestNotes,
    Website_Selected_Topics__c: websiteSelectedTopics,
    UTM_Parameter__c: utmParameter,
    Tech_Data_Origin__c: values.Tech_Data_Origin__c || 'W2L',
    LeadSource: values.LeadSource || 'Website',
    Source_URL__c: values.Source_URL__c || sourceUrl || 'https://smzh.ch/de',
  };

  const formData = new FormData();
  Object.entries(formattedData).forEach(([key, value]) => {
    formData.append(key, value?.toString() || '');
  });
  formData.append('preferredLanguage', getPreferredLanguage());
  appendHiddenFields(
    formData,
    hiddenFields,
    new Set(Object.keys(formattedData)),
  );

  try {
    const response = await submitLeadFormData(formData);
    if (!response.ok) {
      const error = new Error(
        `Lead submission failed with status ${response.status}`,
      );
      console.error(
        'Form submission failed:',
        response.status,
        response.statusText,
      );
      Sentry.captureException(error);
    }
    return response.ok;
  } catch (error) {
    console.error('Form submission error:', error);
    Sentry.captureException(error);
    return false;
  }
};
